import React from "react";
import { Check, Baby, User, Building2, Languages, BadgeCheck, Users, Briefcase } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/site/Reveal";
import { BtnPrimary, BtnSecondary, BtnAccent } from "@/components/site/Buttons";

const IMG = {
  english: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/wwfnns3a_FB_IMG_1770394122069.jpg",
  kids: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/ck2pgh6x_FB_IMG_1778150267894.webp",
  community1: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/nuk5bkzd_FB_IMG_1778150222725.webp",
  community2: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/ck2pgh6x_FB_IMG_1778150267894.webp",
  workplace: "https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
};

const Placeholder = ({ children }) => (
  <p className="inline-block text-xs uppercase tracking-widest text-brand bg-aqua rounded-full px-4 py-2 font-semibold mt-2">{children}</p>
);

const SectionHead = ({ kicker, title, dark }) => (
  <div className="max-w-2xl mb-12">
    <Kicker dark={dark}>{kicker}</Kicker>
    <h2 className={`font-heading font-extrabold text-4xl md:text-5xl tracking-tight ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
  </div>
);

export default function Services() {
  const { tr } = useLang();
  const s = tr.services;

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-navy text-white grain">
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker dark>{tr.nav.services}</Kicker>
            <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6">{s.heroTitle}</h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto mb-8">{s.heroSub}</p>
            <BtnAccent to="/contact" testid="services-hero-cta">{s.heroCta}</BtnAccent>
          </Reveal>
        </div>
      </section>

      {/* SPANISH PATHWAY */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHead kicker={s.spanishKicker} title={s.spanishTitle} /></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {s.spanishCourses.map((c, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div data-testid={`spanish-course-${i}`} className="bg-white rounded-3xl p-8 border border-navy/5 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-heading font-extrabold text-4xl text-brand/20">0{i + 1}</span>
                    <h3 className="font-heading font-bold text-2xl text-navy">{c.title}</h3>
                  </div>
                  <p className="text-sm mb-5"><span className="font-bold text-navy">{s.bestFor}: </span><span className="text-ink/70">{c.best}</span></p>
                  <p className="text-xs uppercase tracking-widest text-brand font-bold mb-3">{s.focusAreas}</p>
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mb-7 flex-1">
                    {c.focus.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-ink/70"><Check size={16} className="text-gold mt-0.5 shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <BtnSecondary to="/contact" testid={`spanish-ask-${i}`} className="!px-5 !py-2.5 text-sm w-fit">{tr.common.askAboutClass}</BtnSecondary>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGLISH */}
      <section className="py-24 lg:py-32 bg-aqua/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionHead kicker={s.englishKicker} title={s.englishTitle} />
            <div className="space-y-5">
              {s.englishCourses.map((c, i) => (
                <div key={i} data-testid={`english-course-${i}`} className="bg-white rounded-3xl p-7 border border-navy/5">
                  <h3 className="font-heading font-bold text-xl text-navy mb-2">{c.title}</h3>
                  <p className="text-ink/65 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6"><BtnPrimary to="/contact" testid="english-cta">{tr.common.askAboutClass}</BtnPrimary></div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.15)] aspect-[4/3]">
              <img src={IMG.english} alt="Adults participating in a language class at Alma y Lengua" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* KIDS + TUTORING */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div data-testid="kids-card" className="bg-navy text-white grain rounded-[2rem] p-10 h-full">
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white/10 text-gold grid place-items-center mb-6"><Baby size={26} /></div>
                <Kicker dark>{s.kidsKicker}</Kicker>
                <h3 className="font-heading font-extrabold text-3xl mb-5">{s.kidsTitle}</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-6">{s.kidsBody}</p>
                <div className="rounded-2xl overflow-hidden aspect-[16/10]">
                  <img src={IMG.kids} alt="Children learning Spanish on a colorful alphabet rug at Alma y Lengua" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div data-testid="tutoring-card" className="bg-white rounded-[2rem] p-10 border border-navy/5 h-full">
              <div className="w-14 h-14 rounded-2xl bg-aqua text-brand grid place-items-center mb-6"><User size={26} /></div>
              <Kicker>{s.tutorKicker}</Kicker>
              <h3 className="font-heading font-extrabold text-3xl text-navy mb-5">{s.tutorTitle}</h3>
              <p className="text-ink/70 text-lg leading-relaxed mb-5">{s.tutorBody}</p>
              <p className="text-sm font-bold text-navy mb-3">{s.tutorGoalsIntro}</p>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mb-7">
                {s.tutorGoals.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink/70"><Check size={16} className="text-gold mt-0.5 shrink-0" />{g}</li>
                ))}
              </ul>
              <BtnPrimary to="/contact" testid="tutoring-cta">{s.tutorCta}</BtnPrimary>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WORKPLACE */}
      <section className="py-24 lg:py-32 bg-aqua/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal delay={0.1} className="order-2 lg:order-1">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.15)] aspect-[4/3]">
              <img src={IMG.workplace} alt="Employees participating in workplace language training" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <div className="w-14 h-14 rounded-2xl bg-navy text-gold grid place-items-center mb-6"><Building2 size={26} /></div>
            <SectionHead kicker={s.workKicker} title={s.workTitle} />
            <p className="text-lg text-ink/70 leading-relaxed mb-5">{s.workBody}</p>
            <p className="text-sm font-bold text-navy mb-3">{s.workTopicsIntro}</p>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mb-7">
              {s.workTopics.map((tp, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink/70"><Check size={16} className="text-gold mt-0.5 shrink-0" />{tp}</li>
              ))}
            </ul>
            <BtnAccent to="/contact" testid="workplace-services-cta">{s.workCta}</BtnAccent>
          </Reveal>
        </div>
      </section>

      {/* BILINGUAL BUSINESS ACADEMY HIGHLIGHT */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div data-testid="academy-highlight" className="rounded-[2.5rem] bg-white border border-navy/5 shadow-[0_20px_60px_rgba(7,20,63,0.08)] p-10 md:p-14 grid lg:grid-cols-[auto_1fr_auto] gap-8 items-center">
              <div className="w-16 h-16 rounded-2xl bg-gold text-navy grid place-items-center shrink-0"><Briefcase size={30} /></div>
              <div>
                <Kicker>{tr.academy.promoKicker}</Kicker>
                <h2 className="font-heading font-extrabold text-navy text-3xl md:text-4xl tracking-tight mb-3">{tr.academy.promoTitle}</h2>
                <p className="text-ink/70 text-lg leading-relaxed max-w-2xl">{tr.academy.promoBlurb}</p>
              </div>
              <BtnPrimary to="/business-academy" testid="academy-highlight-cta" className="shrink-0">{tr.academy.promoCta}</BtnPrimary>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRANSLATION + CITIZENSHIP */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div data-testid="translation-card" className="bg-white rounded-[2rem] p-10 border border-navy/5 h-full">
              <div className="w-14 h-14 rounded-2xl bg-aqua text-brand grid place-items-center mb-6"><Languages size={26} /></div>
              <Kicker>{s.transKicker}</Kicker>
              <h3 className="font-heading font-extrabold text-3xl text-navy mb-5">{s.transTitle}</h3>
              <p className="text-ink/70 text-lg leading-relaxed mb-5">{s.transBody}</p>
              <p className="text-sm text-ink/60 bg-offwhite rounded-2xl p-5 border border-navy/5 mb-7">{s.transNote}</p>
              <BtnPrimary to="/contact" testid="translation-cta">{s.transCta}</BtnPrimary>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div data-testid="citizenship-card" className="bg-white rounded-[2rem] p-10 border border-navy/5 h-full">
              <div className="w-14 h-14 rounded-2xl bg-aqua text-brand grid place-items-center mb-6"><BadgeCheck size={26} /></div>
              <Kicker>{s.citizenKicker}</Kicker>
              <h3 className="font-heading font-extrabold text-3xl text-navy mb-5">{s.citizenTitle}</h3>
              <p className="text-ink/70 text-lg leading-relaxed">{s.citizenBody}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="rounded-[2.5rem] bg-navy text-white grain p-10 md:p-16 relative overflow-hidden">
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-white/10 text-gold grid place-items-center mb-6"><Users size={26} /></div>
                <Kicker dark>{s.commKicker}</Kicker>
                <h2 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight mb-6">{s.commTitle}</h2>
                <p className="text-white/70 text-lg leading-relaxed mb-8">{s.commBody}</p>
                <BtnAccent to="/contact" testid="services-community-cta">{tr.home.commCta}</BtnAccent>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden aspect-[3/4] col-span-1">
                  <img src={IMG.community1} alt="Families gathered at a Bilingual Exploradores community event" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden aspect-[3/4] col-span-1 mt-8">
                  <img src={IMG.community2} alt="Children and families learning together at a Bilingual Exploradores program" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
